import type { Bundle, DocumentReference } from 'fhir/r4';

import FHIRFetcher from '../../../data/FHIRFetcher';

const NOTE_TYPE_SYSTEM = 'https://projectwell.io/fhir/identifiers/note-type';
const NOTE_TYPE_NOTE = 'note';
const NOTE_TYPE_TASK = 'task';
const NOTE_TYPE_MEMBER_DOCUMENT = 'member-document';

/** FHIR identifier system used to distinguish note vs member-document DocumentReferences. */
export const DOCUMENT_REFERENCE_NOTE_TYPE_SYSTEM = NOTE_TYPE_SYSTEM;

/** `identifier.value` for member file uploads created in Care Betty. */
export const DOCUMENT_REFERENCE_MEMBER_DOCUMENT_TYPE = NOTE_TYPE_MEMBER_DOCUMENT;

const NOTE_LIST_ELEMENTS = [
  'id',
  'meta',
  'status',
  'subject',
  'author',
  'date',
  'description',
  'category',
  'type',
  'identifier',
  'relatesTo',
].join(',');

const MEMBER_DOCUMENT_LIST_ELEMENTS = [
  'id',
  'meta',
  'status',
  'subject',
  'author',
  'date',
  'description',
  'identifier',
  'extension',
  'context',
  'content.attachment.title',
  'content.attachment.contentType',
].join(',');

const MEMBER_DOCUMENT_ORG_EXTENSION_URL = 'https://projectwell.io/fhir/orgid';

/**
 * Metadata-only DocumentReference search for text notes.
 *
 * @remarks
 * Omits `content` to stay under API payload limits. fhir-proxy merges PW and payor store
 * results into one bundle per request. Tagged and legacy notes are split client-side via
 * {@link isCareBettyNoteDocumentReference}.
 */
export function buildPatientNotesSearchUrl(patientId: string): string {
  return (
    `/DocumentReference/?patient=${encodeURIComponent(patientId)}` +
    `&status:not=entered-in-error` +
    `&description:missing=false` +
    `&_elements=${NOTE_LIST_ELEMENTS}` +
    `&_sort=-date&_count=500`
  );
}

/**
 * Metadata-only DocumentReference search for member file uploads.
 *
 * @remarks
 * Requests `content.attachment.title` and `contentType` only (no base64). fhir-proxy post-filters nested `_elements`.
 * `category:missing=true` excludes text notes, which always carry a LOINC category.
 * Survey uploads are included via {@link isSurveyUploadedDocumentReference} (`context.related` → QuestionnaireResponse).
 * fhir-proxy merges PW and payor results in one request; {@link isMemberFileUpload} classifies rows.
 */
export function buildMemberDocumentsSearchUrl(patientId: string): string {
  return (
    `/DocumentReference/?patient=${encodeURIComponent(patientId)}` +
    `&status:not=entered-in-error` +
    `&category:missing=true` +
    `&_elements=${MEMBER_DOCUMENT_LIST_ELEMENTS}` +
    `&_sort=-date&_count=200`
  );
}

/** Reads a single DocumentReference by id (full resource, including any attachment payload). */
export function buildDocumentReferenceReadUrl(documentId: string, patientId: string): string {
  return (
    `/DocumentReference/${encodeURIComponent(documentId)}` +
    `?patient=${encodeURIComponent(patientId)}`
  );
}

/** Reads a single DocumentReference, including attachment payload, for download. */
export function buildMemberDocumentReadUrl(documentId: string, patientId: string): string {
  return buildDocumentReferenceReadUrl(documentId, patientId);
}

/**
 * Original uploaded file name from list metadata (`content.attachment.title`).
 */
export function getMemberDocumentFileName(resource: DocumentReference): string | undefined {
  return resource.content?.[0]?.attachment?.title?.trim() || undefined;
}

function getNoteTypeValue(resource: DocumentReference): string | undefined {
  return resource.identifier?.find((identifier) => identifier.system === NOTE_TYPE_SYSTEM)?.value;
}

/**
 * Returns true for Care Betty text notes, including legacy notes without a `note-type` identifier.
 */
export function isCareBettyNoteDocumentReference(resource: DocumentReference): boolean {
  const noteType = getNoteTypeValue(resource);
  if (noteType === NOTE_TYPE_NOTE) {
    return true;
  }
  if (noteType === NOTE_TYPE_TASK || noteType === NOTE_TYPE_MEMBER_DOCUMENT) {
    return false;
  }

  const hasDescription = Boolean(resource.description?.trim());
  const hasClinicalShape = Boolean(resource.category?.length || resource.type?.coding?.length);
  return hasDescription && hasClinicalShape;
}

function hasMemberDocumentOrgExtension(resource: DocumentReference): boolean {
  return Boolean(
    resource.extension?.some(
      (extension) => extension.url === MEMBER_DOCUMENT_ORG_EXTENSION_URL && Boolean(extension.valueString?.trim())
    )
  );
}

/**
 * Returns true when a DocumentReference was created from a survey file-upload answer.
 */
export function isSurveyUploadedDocumentReference(resource: DocumentReference): boolean {
  return Boolean(
    resource.context?.related?.some((related) => isQuestionnaireResponseReference(related.reference))
  );
}

function isQuestionnaireResponseReference(reference?: string): boolean {
  if (!reference?.trim()) {
    return false;
  }

  const normalized = reference.trim();
  return /^QuestionnaireResponse\//i.test(normalized) || /^QR\//i.test(normalized);
}

/**
 * Display value for the member-document "Uploaded By" column.
 */
export function getMemberDocumentUploadedBy(resource: DocumentReference): string {
  if (isSurveyUploadedDocumentReference(resource)) {
    return 'Survey';
  }

  return resource.author?.[0]?.display?.trim() || 'Unknown';
}

/**
 * Returns true for member file uploads (DocumentReferences with embedded attachment data, not text notes).
 */
export function isMemberFileUpload(resource: DocumentReference): boolean {
  const noteType = getNoteTypeValue(resource);
  if (noteType === NOTE_TYPE_MEMBER_DOCUMENT) {
    return true;
  }
  if (noteType === NOTE_TYPE_NOTE || noteType === NOTE_TYPE_TASK) {
    return false;
  }

  if (resource.category?.length) {
    return false;
  }

  if (isSurveyUploadedDocumentReference(resource)) {
    return true;
  }

  const attachment = resource.content?.[0]?.attachment;
  return (
    hasMemberDocumentOrgExtension(resource) ||
    Boolean(resource.description?.trim()) ||
    Boolean(attachment?.data) ||
    Boolean(attachment?.title?.trim())
  );
}

function dedupeBundleEntries(entries: Array<{ resource?: DocumentReference }> = []): Array<{ resource: DocumentReference }> {
  const seen = new Set<string>();
  const deduped: Array<{ resource: DocumentReference }> = [];

  entries.forEach((entry) => {
    const resource = entry?.resource;
    const id = resource?.id;
    if (!resource || !id || seen.has(id)) {
      return;
    }
    seen.add(id);
    deduped.push({ resource });
  });

  return deduped;
}

/**
 * Loads patient text notes in one request (PW + payor merged by fhir-proxy).
 */
export async function fetchPatientNotes(token: string, patientId: string): Promise<Bundle> {
  const bundle = await FHIRFetcher(buildPatientNotesSearchUrl(patientId), token);
  // `Bundle.entry` is the whole resource union; these searches ask for
  // DocumentReferences and nothing checks `resourceType` on the way back, so the
  // narrowing is the query's claim rather than a checked fact.
  const entries = dedupeBundleEntries((bundle?.entry ?? []) as { resource?: DocumentReference }[])
    .filter((entry) => isCareBettyNoteDocumentReference(entry.resource))
    .filter((entry) => Boolean(entry.resource.description?.trim()));

  return {
    resourceType: 'Bundle',
    type: 'searchset',
    entry: entries,
  };
}

/**
 * Loads member-document metadata in one request. Attachment bytes are fetched per document on download.
 */
export async function fetchMemberDocumentMetadata(token: string, patientId: string): Promise<Bundle> {
  const bundle = await FHIRFetcher(buildMemberDocumentsSearchUrl(patientId), token);
  const entries = dedupeBundleEntries((bundle?.entry ?? []) as { resource?: DocumentReference }[]).filter(
    (entry) => isMemberFileUpload(entry.resource)
  );

  return {
    resourceType: 'Bundle',
    type: 'searchset',
    entry: entries,
  };
}

/**
 * Loads a single DocumentReference with full resource content.
 */
export async function fetchDocumentReference(
  token: string,
  documentId: string,
  patientId: string
): Promise<DocumentReference> {
  // `FHIRFetcher` resolves `undefined` for an empty endpoint; this one is built
  // from two ids and is never empty.
  return FHIRFetcher(buildDocumentReferenceReadUrl(documentId, patientId), token) as Promise<DocumentReference>;
}

/**
 * Loads a single member document with attachment data for download.
 */
export async function fetchMemberDocumentWithAttachment(
  token: string,
  documentId: string,
  patientId: string
): Promise<DocumentReference> {
  return fetchDocumentReference(token, documentId, patientId);
}
